import { memo, useCallback, useEffect, useMemo, useState, useRef } from "react";
import type { ExpressionSpecification } from "maplibre-gl";
import { useQueries, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { Map as MapLibreContainer, MapControls, MapGeoJSON, type MapGeoJSONEvent } from "@/components/ui/map";
import type {
  MapChange,
  MapChangeRequest,
  MapFeatureValue,
  MapPeriods,
  MapSlice,
} from "@/features/explorer/api/contracts";
import { explorerQueryKeys, ANALYTICS_STALE_TIME_MS, ANALYTICS_GC_TIME_MS } from "@/features/explorer/api/query-keys";
import { metricCatalogue } from "@/features/explorer/data/catalogue";
import { getMapChange, getMapPeriods, getMapSlice } from "@/features/explorer/services/national-data-api";
import type {
  CompositeFeatureResult,
  CompositeWeightMap,
  ExplorerSelection,
  MetricDefinition,
  MetricId,
} from "@/features/explorer/types";
import { computeCompositeScores, isMetricId } from "../lib/composite-index";
import { loadBoundaryFeatureCollection } from "@/lib/boundaries";
import { useThemeToggle } from "@/hooks/use-theme-toggle";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  computeColorBins,
  computeStatistics,
  createChoroplethExpression,
  formatChangeValue,
  formatMetricValue,
  formatZScore,
  formatZScoreInterpretation,
} from "../lib/map-utils";
import { ExplorerComparisonFlyout } from "./ExplorerComparisonFlyout";
import { ExplorerControls } from "./ExplorerControls";
import { ExplorerWelcomeModal } from "./ExplorerWelcomeModal";
import { ExplorerChangeToggle, type ChangeBaseline, type ChangeMode } from "./ExplorerChangeToggle";
import { formatPeriodLabel } from "@/features/explorer/lib/format";
import { CoverageBadge } from "./CoverageBadge";
import { PeriodTimeline } from "./PeriodTimeline";
import { RankingStrip } from "./RankingStrip";
import { useExplorerJoyride } from "@/features/explorer/hooks/use-explorer-joyride";
import { ExplorerJoyride } from "@/features/explorer/components/ExplorerJoyride";
import { setExplorerWelcomeModalDismissed, shouldShowExplorerWelcomeModal } from "../lib/welcome-modal-state";
import {
  RefreshCw,
  Info,
  SlidersHorizontal,
  ArrowRightLeft,
  Layers,
  X,
  TrendingUp,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { firstString } from "@/lib/props";

type ExplorerMapPanelProps = {
  metric: MetricDefinition;
  selection: ExplorerSelection;
  onSelectionChange: (selection: ExplorerSelection) => void;
  initialCompareCode?: string;
  compositeWeights: CompositeWeightMap;
  onCompositeWeightsChange: (weights: CompositeWeightMap) => void;
  pinnedCodes: readonly string[];
  onPinnedCodesChange: (codes: string[]) => void;
  onNavExpandChange?: (expanded: boolean) => void;
};

type GeoJSONFeatureCollection = GeoJSON.FeatureCollection<
  GeoJSON.Geometry,
  {
    geographyCode: string;
    name: string;
    value?: number | null;
    isSuppressed?: boolean;
    qualityFlag?: string | null;
  }
>;

type SelectedRegionInfo = {
  geographyCode: string;
  name: string;
  value: number | null;
  qualityFlag?: string | null;
};

const EMPTY_VALUES: MapFeatureValue[] = [];
const EMPTY_COMPOSITE_SCORES: ReadonlyMap<string, CompositeFeatureResult> = new Map();

/**
 * Memoized map canvas. Holds only the data-driven inputs the map depends on, so
 * unrelated parent state churn (hover, flyout, panels) does not re-render the
 * MapLibre instance or re-reconcile the choropleth source/layers. Hover/click
 * are reported via stable callbacks so the memo isn't defeated.
 */
const ExplorerMapCanvas = memo(
  ({
    geoJson,
    featureValuesById,
    fillStyle,
    boundaryStrokeColor,
    hoverColor,
    onHover,
    onClick,
  }: {
    geoJson: GeoJSONFeatureCollection | null;
    featureValuesById?:
      | ReadonlyMap<string, { value: number | null; isSuppressed: boolean; qualityFlag: string | null }>
      | undefined;
    fillStyle: ExpressionSpecification;
    boundaryStrokeColor: string;
    hoverColor: string;
    onHover: (e: MapGeoJSONEvent | null) => void;
    onClick: (e: MapGeoJSONEvent) => void;
  }) => {
    return (
      <MapLibreContainer center={[-1.8, 52.8]} zoom={6.4} minZoom={4} maxZoom={10} blank>
        <MapControls position='bottom-right' showZoom showCompass showFullscreen />

        {geoJson && (
          <MapGeoJSON
            id='la-choropleth'
            data={geoJson}
            promoteId='geographyCode'
            featureValuesById={featureValuesById}
            fillPaint={{
              "fill-color": fillStyle,
              "fill-opacity": 0.88,
            }}
            linePaint={{
              "line-color": boundaryStrokeColor,
              "line-width": 1.5,
              "line-opacity": 1,
            }}
            fillHoverPaint={{
              "fill-opacity": 1,
              "fill-color": hoverColor,
            }}
            interactive
            onHover={onHover}
            onClick={onClick}
          />
        )}
      </MapLibreContainer>
    );
  },
);
ExplorerMapCanvas.displayName = "ExplorerMapCanvas";

export function ExplorerMapPanel({
  metric,
  selection,
  onSelectionChange,
  initialCompareCode,
  compositeWeights,
  onCompositeWeightsChange,
  pinnedCodes,
  onPinnedCodesChange,
  onNavExpandChange,
}: ExplorerMapPanelProps) {
  const queryClient = useQueryClient();
  const { isDark } = useThemeToggle();
  const joyride = useExplorerJoyride();

  // Notify parent page when Step 8 ("export-nav") is active to force-expand navbar flyout
  useEffect(() => {
    if (!joyride.isRunning) {
      onNavExpandChange?.(false);
      return;
    }
    const isStep8 = joyride.currentStep.id === "export-nav";
    onNavExpandChange?.(isStep8);
  }, [joyride.isRunning, joyride.currentStep.id, onNavExpandChange]);
  // Phones and small/medium tablets (< lg) get the mobile experience. The
  // desktop floating layout is reserved for large tablets and laptops.
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const isLg = useMediaQuery("(min-width: 1024px) and (max-width: 1279px)");

  const [hoveredInfo, setHoveredInfo] = useState<SelectedRegionInfo | null>(null);
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(true);

  // On LG screens, automatically collapse Overview card when hovering a region so hover tooltip card has room
  const effectiveOverviewExpanded = isOverviewExpanded && !(isLg && Boolean(hoveredInfo));

  const [primaryRegion, setPrimaryRegion] = useState<SelectedRegionInfo | null>(null);
  const [secondaryRegion, setSecondaryRegion] = useState<SelectedRegionInfo | null>(null);
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(() => shouldShowExplorerWelcomeModal(globalThis.localStorage));

  // Derived state pattern for reacting to prop changes without cascading useEffect renders
  const [prevGeoLevel, setPrevGeoLevel] = useState(selection.geographyLevel);
  if (selection.geographyLevel !== prevGeoLevel) {
    setPrevGeoLevel(selection.geographyLevel);
    setPrimaryRegion(null);
    setSecondaryRegion(null);
    setIsFlyoutOpen(false);
  }

  // Accordion & Mobile Sheet Active Tab state: on LG screens, start collapsed ("none") on page load
  const [activePanel, setActivePanel] = useState<"controls" | "comparison" | "none">(() => {
    if (globalThis.window !== undefined) {
      const isLgViewport = globalThis.innerWidth >= 1024 && globalThis.innerWidth < 1280;
      if (isLgViewport) return "none";
    }
    return "controls";
  });
  const [mobileSheetTab, setMobileSheetTab] = useState<"controls" | "overview" | "comparison" | "none">("none");
  const [controlsTab, setControlsTab] = useState<"metrics" | "geography" | "index">("metrics");
  const [isRankingCollapsed, setIsRankingCollapsed] = useState(() => {
    if (globalThis.window !== undefined) {
      return globalThis.innerWidth >= 1024 && globalThis.innerWidth < 1280;
    }
    return false;
  });

  // Dynamic granularity level label for top overview badge
  const granularityLevelLabel = useMemo(() => {
    switch (selection.geographyLevel) {
      case "region": {
        return "Region Level";
      }
      case "county": {
        return "County Level";
      }
      case "local-authority": {
        return "Authority Level";
      }
      default: {
        const exhaustive: never = selection.geographyLevel;
        return exhaustive;
      }
    }
  }, [selection.geographyLevel]);

  const rawGranularityName = useMemo(() => {
    switch (selection.geographyLevel) {
      case "region": {
        return "Region";
      }
      case "county": {
        return "County";
      }
      case "local-authority": {
        return "Local Authority";
      }
      default: {
        const exhaustive: never = selection.geographyLevel;
        return exhaustive;
      }
    }
  }, [selection.geographyLevel]);

  // Sonner Toast notification whenever the displayed dataset or granularity
  // changes. All catalogue labels are unique, so label + source + granularity
  // fully identify the notification content — no id dependency needed.
  useEffect(() => {
    toast.success(`Loaded ${metric.label}`, {
      description: `${granularityLevelLabel} • ${metric.sourceLabel}`,
      duration: 3500,
    });
  }, [metric.label, metric.sourceLabel, granularityLevelLabel]);

  useEffect(() => {
    if (!isWelcomeOpen) {
      setExplorerWelcomeModalDismissed(globalThis.localStorage, true);
    }
  }, [isWelcomeOpen]);

  const activeMetricEntries = useMemo(() => {
    const entries: [MetricId, number][] = [];
    for (const [id, weight] of Object.entries(compositeWeights)) {
      if (typeof weight === "number" && weight > 0 && isMetricId(id)) entries.push([id, weight]);
    }
    return entries;
  }, [compositeWeights]);

  const isCompositeActive = activeMetricEntries.length > 0;

  // Change-view state: switch between absolute values and a delta vs a baseline
  // period. Baseline ("previous" | "yearly") is ignored while composite is
  // active (a weighted index has no single shared baseline).
  const [changeMode, setChangeMode] = useState<ChangeMode>("absolute");
  const [baseline, setBaseline] = useState<ChangeBaseline>("yearly");
  const changeActive = changeMode === "change" && !isCompositeActive;

  // TanStack Query for Single Metric Map Slice
  const {
    data: mapSlice,
    isFetching,
    refetch,
  } = useQuery<MapSlice>({
    queryKey: explorerQueryKeys.mapSlice(selection),
    queryFn: () => getMapSlice(selection),
    placeholderData: keepPreviousData,
    staleTime: ANALYTICS_STALE_TIME_MS,
    gcTime: ANALYTICS_GC_TIME_MS,
    refetchOnWindowFocus: false,
  });

  // Change-view slice: current period minus a resolved baseline period.
  const { data: mapChange } = useQuery<MapChange>({
    queryKey: explorerQueryKeys.mapChange({
      metricId: selection.metricId,
      geographyLevel: selection.geographyLevel,
      statisticId: selection.statisticId,
      period: selection.period,
      baseline,
    }),
    queryFn: () =>
      getMapChange({
        metricId: selection.metricId,
        geographyLevel: selection.geographyLevel,
        statisticId: selection.statisticId,
        period: selection.period,
        baseline,
      }),
    enabled: changeActive,
    placeholderData: keepPreviousData,
    staleTime: ANALYTICS_STALE_TIME_MS,
    gcTime: ANALYTICS_GC_TIME_MS,
    refetchOnWindowFocus: false,
  });

  // Periods for the current metric/statistic; the change view needs at least
  // two periods to resolve a baseline. Shares the PeriodTimeline query cache.
  const { data: mapPeriodsData } = useQuery<MapPeriods>({
    queryKey: explorerQueryKeys.mapPeriods({
      metricId: selection.metricId,
      geographyLevel: selection.geographyLevel,
      statisticId: selection.statisticId,
    }),
    queryFn: () =>
      getMapPeriods({
        metricId: selection.metricId,
        geographyLevel: selection.geographyLevel,
        statisticId: selection.statisticId,
      }),
    staleTime: ANALYTICS_STALE_TIME_MS,
    gcTime: ANALYTICS_GC_TIME_MS,
    refetchOnWindowFocus: false,
  });

  // Background prefetch for the Change view. The top-priority metric data
  // (mapSlice) loads first; once it has settled we quietly warm the cache for
  // the first period comparison (the active baseline) so toggling into the
  // Change view is instant rather than triggering a visible fetch. It is
  // skipped while the change view is already active (its query owns the request).
  const prefetchChangeRequest: MapChangeRequest = useMemo(
    () => ({
      metricId: selection.metricId,
      geographyLevel: selection.geographyLevel,
      statisticId: selection.statisticId,
      period: selection.period,
      baseline,
    }),
    [selection.metricId, selection.geographyLevel, selection.statisticId, selection.period, baseline],
  );

  const mapSliceLoaded = Boolean(mapSlice);
  useEffect(() => {
    if (!mapSliceLoaded || changeActive) return () => {};
    const queryKey = explorerQueryKeys.mapChange(prefetchChangeRequest);
    // A real (useful) payload is already cached — nothing to warm.
    const cached = queryClient.getQueryData<MapChange>(queryKey);
    if (cached && cached.values.length > 0 && cached.baselinePeriod) return () => {};

    let cancelled = false;
    getMapChange(prefetchChangeRequest)
      .then((data) => {
        if (cancelled) return;
        if (data && data.values.length > 0 && data.baselinePeriod) {
          // Cache only a useful payload so toggling the change view is instant.
          queryClient.setQueryData(explorerQueryKeys.mapChange(prefetchChangeRequest), data, {
            updatedAt: Date.now(),
          });
        } else {
          // Empty or failed payload: drop it so the change view issues a real
          // request when it becomes active instead of serving stale nothing.
          queryClient.removeQueries({ queryKey });
        }
      })
      .catch(() => {
        if (!cancelled) queryClient.removeQueries({ queryKey });
      });
    return () => {
      cancelled = true;
    };
  }, [queryClient, mapSliceLoaded, changeActive, prefetchChangeRequest]);

  const hasComparablePeriods = (mapPeriodsData?.periods.length ?? 0) >= 2;
  const showChangeToggle = !isCompositeActive && hasComparablePeriods;

  // Safety net: if a non-"latest" period is not actually available for the
  // current metric/statistic/geography (e.g. a shared URL carried a period
  // from a different dataset, or a period is missing from a rolled-up
  // geography), reset to "latest" so the map never silently loads empty.
  useEffect(() => {
    const periods = mapPeriodsData?.periods;
    if (!periods || periods.length === 0) return;
    if (selection.period === "latest") return;
    if (periods.includes(selection.period)) return;
    // eslint-disable-next-line react-doctor/no-prop-callback-in-effect
    onSelectionChange({ ...selection, period: "latest" });
  }, [mapPeriodsData, selection, onSelectionChange]);

  // The change map is only shown once a baseline has actually been resolved;
  // until then (or if none exists, e.g. the earliest period is selected) the
  // absolute values are rendered instead so the map never appears empty.
  const isChangeView = changeActive && Boolean(mapChange?.baselinePeriod);

  // Each metric is stored in the DB under a specific statistic (e.g. CO2 only
  // exists as "rate", EV charger density only as "count"). The composite builder
  // must query each active metric with a statistic it actually supports rather
  // than reusing the selection's statistic for the currently-viewed metric,
  // otherwise those slices come back empty and the composite choropleth renders
  // no colour.
  const compositeStatisticDefaults = useMemo(
    () => new Map(metricCatalogue.map((m) => [m.id, m.supportedStatistics[0]])),
    [],
  );

  // Query slices for all active weighted metrics in parallel for composite index
  const compositeQueries = useQueries({
    queries: activeMetricEntries.map(([mId]) => {
      const request = {
        ...selection,
        metricId: mId,
        statisticId: compositeStatisticDefaults.get(mId) ?? selection.statisticId,
        // Each metric resolves to its own latest period so a shared period that
        // only exists for one weighted metric never leaves the others empty.
        period: "latest",
      };
      return {
        queryKey: explorerQueryKeys.mapSlice(request),
        queryFn: () => getMapSlice(request),
        staleTime: ANALYTICS_STALE_TIME_MS,
        gcTime: ANALYTICS_GC_TIME_MS,
        enabled: isCompositeActive,
      };
    }),
  });

  const compositeSliceMap = useMemo(() => {
    const map = new Map<MetricId, MapFeatureValue[]>();
    for (let idx = 0; idx < activeMetricEntries.length; idx++) {
      const mId = activeMetricEntries[idx][0];
      const q = compositeQueries[idx];
      if (q?.data?.values) {
        map.set(mId, q.data.values);
      }
    }
    return map;
  }, [activeMetricEntries, compositeQueries]);

  const compositeScores = useMemo(() => {
    if (!isCompositeActive) return EMPTY_COMPOSITE_SCORES;
    return computeCompositeScores(compositeSliceMap, compositeWeights);
  }, [compositeSliceMap, compositeWeights, isCompositeActive]);

  // Targeted TanStack Query Invalidation & Edge Cache Purge Helper
  const handleRefreshAndInvalidate = async () => {
    try {
      const sliceRequest = {
        metricId: selection.metricId,
        geographyLevel: selection.geographyLevel,
        statisticId: selection.statisticId,
        period: "latest",
        refresh: true,
      };
      const freshData = await getMapSlice(sliceRequest);
      // Directly update the React Query cache with the fresh data so we bypass browser HTTP caching
      queryClient.setQueryData(explorerQueryKeys.mapSlice(selection), freshData);

      const periodsRequest = {
        metricId: selection.metricId,
        geographyLevel: selection.geographyLevel,
        statisticId: selection.statisticId,
        refresh: true,
      };
      const freshPeriods = await getMapPeriods(periodsRequest);
      queryClient.setQueryData(
        explorerQueryKeys.mapPeriods({
          metricId: selection.metricId,
          geographyLevel: selection.geographyLevel,
          statisticId: selection.statisticId,
        }),
        freshPeriods,
      );

      // Change deltas are precomputed by the ingestion pipeline, so purge the
      // cached slice too (browser + edge) so a rebuild reaches the Change view.
      if (changeActive) {
        const changeRequest = {
          metricId: selection.metricId,
          geographyLevel: selection.geographyLevel,
          statisticId: selection.statisticId,
          period: selection.period,
          baseline,
          refresh: true,
        };
        const freshChange = await getMapChange(changeRequest);
        queryClient.setQueryData(explorerQueryKeys.mapChange(changeRequest), freshChange);
      } else {
        queryClient.removeQueries({ queryKey: [...explorerQueryKeys.all, "map-change"] });
      }

      toast.success("Cache Purged & Refetched", {
        description: "Bypassed Edge cache and re-fetched live data.",
      });
    } catch {
      await refetch();
      toast.info("Local cache purged", { description: "Re-querying Aiven PostGIS API" });
    }
  };

  // Fetch boundaries safely through React Query, eliminating raw fetch() useEffect race conditions
  const boundaryPath =
    selection.geographyLevel === "county"
      ? "county"
      : selection.geographyLevel === "region"
        ? "region"
        : "local-authority";

  const { data: baseBoundaries = null } = useQuery<GeoJSONFeatureCollection>({
    queryKey: ["boundaries", boundaryPath],
    queryFn: () => loadBoundaryFeatureCollection(boundaryPath),
    staleTime: Infinity, // GeoJSON boundaries are static, cache indefinitely
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Boundary geometry for the map source. Explicitly memoized on baseBoundaries
  // ALONE so the reference stays stable across metric/composite changes - value
  // updates are applied separately via featureValuesById using MapLibre's
  // updateData diff, avoiding a full geometry re-parse on every selection change.
  const choroplethGeoJSON = useMemo(() => {
    if (!baseBoundaries) return null;
    return {
      type: "FeatureCollection" as const,
      features: baseBoundaries.features.map((feature) => {
        const rawProps = feature.properties as Record<string, unknown>;
        return {
          ...feature,
          properties: {
            ...feature.properties,
            geographyCode: firstString(rawProps.geographyCode, rawProps.code, rawProps.gssCode),
          },
        };
      }),
    };
  }, [baseBoundaries]);

  // Per-feature observation values keyed by the promoted id (geographyCode). Kept
  // separate from geometry so metric/composite changes hit the updateData diff
  // path instead of re-seeding (and re-parsing) the boundary geometry.
  const featureValuesById = useMemo(() => {
    if (!baseBoundaries) return undefined;

    const valueMap = new Map<string, MapFeatureValue>();
    if (isCompositeActive && compositeScores.size > 0) {
      for (const [code, res] of compositeScores.entries()) {
        valueMap.set(code, {
          geographyCode: code,
          value: res.compositeScore,
          isSuppressed: false,
          qualityFlag: null,
        });
      }
    } else if (isChangeView && mapChange?.values) {
      for (const val of mapChange.values) {
        if (!valueMap.has(val.geographyCode)) {
          valueMap.set(val.geographyCode, val);
        }
      }
    } else if (mapSlice?.values) {
      for (const val of mapSlice.values) {
        if (!valueMap.has(val.geographyCode)) {
          valueMap.set(val.geographyCode, val);
        }
      }
    }

    const byId = new Map<string, { value: number | null; isSuppressed: boolean; qualityFlag: string | null }>();
    for (const feature of baseBoundaries.features) {
      const rawProps = feature.properties as Record<string, unknown>;
      const code = firstString(rawProps.geographyCode, rawProps.code, rawProps.gssCode);
      const obs = valueMap.get(code);
      byId.set(code, {
        value: obs ? obs.value : null,
        isSuppressed: obs ? obs.isSuppressed : false,
        qualityFlag: obs ? obs.qualityFlag : null,
      });
    }
    return byId;
  }, [baseBoundaries, mapSlice, mapChange, isCompositeActive, compositeScores, isChangeView]);

  // One-time effect: when navigating from the Leaderboard with ?compare=CODE,
  // pre-seed the secondary region once boundary + observation data are both loaded.
  const compareInitialised = useRef(false);
  useEffect(() => {
    if (compareInitialised.current || !initialCompareCode || !baseBoundaries || !mapSlice) return;

    const feature = baseBoundaries.features.find((f) => f.properties.geographyCode === initialCompareCode);

    if (!feature) return;
    const name = feature.properties.name;
    const obsEntry = mapSlice.values.find((v) => v.geographyCode === initialCompareCode);

    // eslint-disable-next-line react-hooks-js/set-state-in-effect
    setSecondaryRegion({
      geographyCode: initialCompareCode,
      name,
      value: obsEntry?.value ?? null,
      qualityFlag: obsEntry?.qualityFlag ?? null,
    });
    setMobileSheetTab("comparison");
    setIsFlyoutOpen(true);
    setIsWelcomeOpen(false); // Dismiss welcome modal if showing
    compareInitialised.current = true;
  }, [baseBoundaries, mapSlice, initialCompareCode]);

  // Extract numerical values for color bins and statistical summaries
  const validValues = useMemo(() => {
    if (isChangeView) {
      if (!mapChange?.values) return [];
      return mapChange.values.map((v) => v.value).filter((val): val is number => val !== null && !Number.isNaN(val));
    }
    if (isCompositeActive) {
      return [...compositeScores.values()].map((v) => v.compositeScore);
    }
    if (!mapSlice?.values) return [];
    return mapSlice.values.map((v) => v.value).filter((val): val is number => val !== null && !Number.isNaN(val));
  }, [isCompositeActive, compositeScores, mapSlice, isChangeView, mapChange]);

  const activeUnit = isCompositeActive ? "score" : metric.unit;
  const activeMetricId = isCompositeActive ? "composite-index" : selection.metricId;
  const activeMetricLabel = isCompositeActive ? "Custom Quality-of-Life Index" : metric.label;
  const activeMetricDescription = isCompositeActive
    ? `Live weighted score (0–100) combining ${activeMetricEntries.length} active metric${activeMetricEntries.length > 1 ? "s" : ""}.`
    : metric.description;

  // Look up a readable area name from the boundary features by geography code.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    if (!baseBoundaries) return map;
    for (const feature of baseBoundaries.features) {
      const props = feature.properties as Record<string, unknown> | null;
      if (!props) continue;
      const code = firstString(props.geographyCode, props.code, props.gssCode);
      const name = props.name;
      if (code && typeof name === "string" && name) map.set(code, name);
    }
    return map;
  }, [baseBoundaries]);

  // Auto-expand accordions, switch tabs, and invoke region comparison during Joyride steps
  useEffect(() => {
    if (!joyride.isRunning) return () => {};

    const stepId = joyride.currentStep.id;
    const animId = globalThis.requestAnimationFrame(() => {
      // Mobile: guide through the bottom tab bar / slide-up sheet. Tab buttons
      // are the tour targets for panel entry steps, so close any open sheet so
      // they stay visible; steps that live inside a sheet open the right one.
      if (isMobile) {
        switch (stepId) {
          case "overview-card":
          case "map-controls":
          case "region-comparison":
          case "export-nav": {
            setMobileSheetTab("none");
            break;
          }
          case "composite-tab":
          case "composite-pane": {
            setMobileSheetTab("controls");
            setControlsTab("index");
            break;
          }
          case "ranking-strip": {
            setMobileSheetTab("overview");
            break;
          }
        }
        return;
      }

      switch (stepId) {
        case "overview-card": {
          setIsOverviewExpanded(true);
          break;
        }
        case "map-controls": {
          setActivePanel("controls");
          setControlsTab("metrics");
          break;
        }
        case "composite-tab": {
          setActivePanel("controls");
          setControlsTab("index");
          break;
        }
        case "composite-pane": {
          setActivePanel("controls");
          setControlsTab("index");
          break;
        }
        case "region-comparison": {
          if (featureValuesById && featureValuesById.size >= 2) {
            const keys = [...featureValuesById.keys()];
            const codeA = keys[0];
            const codeB = keys[1];
            const valA = featureValuesById.get(codeA);
            const valB = featureValuesById.get(codeB);
            // Blank names fall back to the code so every region labels itself.
            // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- empty-string names fall through to the code by design.
            const nameA = nameById.get(codeA) || codeA;
            // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- empty-string names fall through to the code by design.
            const nameB = nameById.get(codeB) || codeB;

            setPrimaryRegion({
              geographyCode: codeA,
              name: nameA,
              value: valA?.value ?? null,
              qualityFlag: valA?.qualityFlag ?? null,
            });
            setSecondaryRegion({
              geographyCode: codeB,
              name: nameB,
              value: valB?.value ?? null,
              qualityFlag: valB?.qualityFlag ?? null,
            });
            setIsFlyoutOpen(true);
            setActivePanel("comparison");
          }
          break;
        }
        case "ranking-strip": {
          setActivePanel("none");
          setIsRankingCollapsed(false);
          break;
        }
        case "export-nav": {
          setActivePanel("none");
          break;
        }
      }
    });

    return () => {
      globalThis.cancelAnimationFrame(animId);
    };
  }, [joyride.isRunning, joyride.currentStep.id, featureValuesById, nameById, isMobile]);

  const rankingVisible = !isCompositeActive && changeMode === "absolute";
  const rankingValues = mapSlice?.values ?? EMPTY_VALUES;

  const changeValues = useMemo(
    () => (isChangeView ? (mapChange?.values ?? EMPTY_VALUES) : EMPTY_VALUES),
    [isChangeView, mapChange],
  );
  const activeCoverage = isChangeView ? mapChange?.coverage : mapSlice?.coverage;

  const suppressedCount = useMemo(
    () => (isChangeView ? changeValues : (mapSlice?.values ?? EMPTY_VALUES)).filter((v) => v.isSuppressed).length,
    [isChangeView, changeValues, mapSlice],
  );

  const formatViewValue = (value: number | null): string =>
    isChangeView ? formatChangeValue(value, activeUnit) : formatMetricValue(value, activeUnit);

  const bins = useMemo(
    () => computeColorBins(validValues, activeMetricId, activeUnit),
    [validValues, activeMetricId, activeUnit],
  );
  const stats = useMemo(() => computeStatistics(validValues), [validValues]);
  const fillColorExpression = useMemo(() => createChoroplethExpression(bins), [bins]);

  // Dynamic theming for boundary strokes
  const boundaryStrokeColor = isDark ? "#303446" : "#eff1f5";
  const hoverForegroundColor = isDark ? "#c6d0f5" : "#4c4f69";

  // Handle map region selection for comparisons
  const handleFeatureClick = (props: Record<string, unknown> | null | undefined) => {
    const safeProps = props ?? {};
    const geographyCode = typeof safeProps.geographyCode === "string" ? safeProps.geographyCode : "";
    const name = typeof safeProps.name === "string" ? safeProps.name : rawGranularityName;
    const value = typeof safeProps.value === "number" ? safeProps.value : null;
    const qualityFlag = typeof safeProps.qualityFlag === "string" ? safeProps.qualityFlag : null;

    const info: SelectedRegionInfo = {
      geographyCode,
      name,
      value,
      qualityFlag,
    };

    if (!primaryRegion || primaryRegion.geographyCode === info.geographyCode) {
      setPrimaryRegion(info);
    } else {
      setSecondaryRegion(info);
    }

    setIsFlyoutOpen(true);
    if (isMobile) {
      setMobileSheetTab("comparison");
      setIsFlyoutOpen(true);
    } else {
      setActivePanel("comparison");
    }
  };

  const handleSwapRegionsStable = useCallback(() => {
    setPrimaryRegion(secondaryRegionRef.current);
    setSecondaryRegion(primaryRegionRef.current);
  }, []);

  // Pinned areas: append in pin order, remove on un-pin. Mirrored into the URL
  // by the explorer page so the strip (and its ordering) survives a refresh.
  const handleTogglePin = useCallback(
    (code: string) => {
      const next = pinnedCodes.includes(code) ? pinnedCodes.filter((c) => c !== code) : [...pinnedCodes, code];
      onPinnedCodesChange(next);
    },
    [pinnedCodes, onPinnedCodesChange],
  );

  // Reuses the regional-comparison flyout so search results (and pinned cards)
  // can surface the full area readout instead of just a highlight.
  const handleFocusRegion = useCallback(
    (info: SelectedRegionInfo) => {
      setPrimaryRegion(info);
      setIsFlyoutOpen(true);
      if (isMobile) {
        setMobileSheetTab("comparison");
      } else {
        setActivePanel("comparison");
      }
    },
    [isMobile],
  );

  // Latest primary/secondary kept in refs (effect-updated every render) so
  // handleSwapRegionsStable stays identity-stable for the memoized flyout.
  // Latest primary/secondary kept in refs (effect-updated every render) so
  // handleSwapRegionsStable stays identity-stable for the memoized flyout.
  const primaryRegionRef = useRef<SelectedRegionInfo | null>(null);
  const secondaryRegionRef = useRef<SelectedRegionInfo | null>(null);
  useEffect(() => {
    // eslint-disable-next-line react-hooks-js/immutability, react-hooks-js/refs
    primaryRegionRef.current = primaryRegion;
    // eslint-disable-next-line react-hooks-js/immutability, react-hooks-js/refs
    secondaryRegionRef.current = secondaryRegion;
  });

  // Stable callbacks for the memoized flyout/controls so hover-driven parent
  // re-renders don't churn them (their props stay referentially identical).
  const handleToggleControls = useCallback(() => {
    setActivePanel((current) => (current === "controls" ? "none" : "controls"));
  }, []);
  const handleToggleComparison = useCallback(() => {
    setActivePanel((current) => (current === "comparison" ? "none" : "comparison"));
  }, []);
  const handleCloseComparison = useCallback(() => {
    setIsFlyoutOpen(false);
    setActivePanel((current) => (current === "comparison" ? "controls" : current));
  }, []);
  const handleResetComposite = useCallback(() => {
    onCompositeWeightsChange({});
  }, [onCompositeWeightsChange]);
  const allValues = useMemo(() => mapSlice?.values ?? EMPTY_VALUES, [mapSlice]);
  const flyoutValues = isCompositeActive || isChangeView ? EMPTY_VALUES : allValues;

  // Referentially-stable map callbacks. The latest hover/click logic lives in a
  // ref (kept fresh by an effect) while the memoized <ExplorerMapCanvas> receives
  // stable props, so it never re-renders when these state values change below.
  const handleHoverRef = useRef<(e: MapGeoJSONEvent | null) => void>(() => {});
  const handleClickRef = useRef<(e: MapGeoJSONEvent) => void>(() => {});
  useEffect(() => {
    handleHoverRef.current = (e) => {
      if (e?.feature) {
        const safeProps = (e.feature.properties as Record<string, unknown> | null | undefined) ?? {};
        setHoveredInfo({
          name: typeof safeProps.name === "string" ? safeProps.name : rawGranularityName,
          geographyCode: typeof safeProps.geographyCode === "string" ? safeProps.geographyCode : "",
          value: typeof safeProps.value === "number" ? safeProps.value : null,
          qualityFlag: typeof safeProps.qualityFlag === "string" ? safeProps.qualityFlag : null,
        });
      } else {
        setHoveredInfo(null);
      }
    };
    handleClickRef.current = (e) => {
      if (e?.feature) {
        handleFeatureClick(e.feature.properties);
      }
    };
  });
  const handleMapHover = useCallback((e: MapGeoJSONEvent | null) => {
    handleHoverRef.current(e);
  }, []);
  const handleMapClick = useCallback((e: MapGeoJSONEvent) => {
    handleClickRef.current(e);
  }, []);

  return (
    <section
      id='tour-map-canvas'
      aria-label='UK metric map'
      className='explorer-shell relative size-full overflow-hidden font-sans bg-background'>
      {/* SEO & Getting Started Welcome Modal */}
      <ExplorerWelcomeModal
        isOpen={isWelcomeOpen}
        onStartTour={joyride.handleStartTour}
        onClose={() => {
          setIsWelcomeOpen(false);
          setExplorerWelcomeModalDismissed(globalThis.localStorage, true);
          joyride.handleWelcomeDismissed();
        }}
      />

      {/* Interactive Joyride Tour Overlay */}
      <ExplorerJoyride
        isRunning={joyride.isRunning}
        stepIndex={joyride.stepIndex}
        totalSteps={joyride.totalSteps}
        currentStep={joyride.currentStep}
        onNext={joyride.handleNextStep}
        onPrev={joyride.handlePrevStep}
        onStop={joyride.handleStopTour}
      />

      {/* Immersive Fullscreen Map Canvas */}
      <ExplorerMapCanvas
        geoJson={choroplethGeoJSON}
        featureValuesById={featureValuesById}
        fillStyle={fillColorExpression}
        boundaryStrokeColor={boundaryStrokeColor}
        hoverColor={hoverForegroundColor}
        onHover={handleMapHover}
        onClick={handleMapClick}
      />

      {/* Desktop Floating Columns (md: and above) */}
      {!isMobile && (
        <>
          {/* Desktop Top Left Floating Column: Overview Card + Hover Tooltip */}
          <div className='pointer-events-auto absolute top-4 left-4 z-20 flex w-84 max-w-[calc(100vw-2rem)] flex-col gap-3 font-sans sm:left-6 sm:w-96'>
            {/* Overview & Embedded Legend Card */}
            <div
              id='tour-overview-card'
              className='explorer-surface w-full rounded-lg border border-border p-4.5 text-card-foreground shadow-2xl backdrop-blur-2xl transition-[opacity,transform] duration-200'>
              {effectiveOverviewExpanded ? (
                <>
                  <div className='mb-2 flex items-center justify-between gap-2'>
                    <span className='inline-flex h-7 shrink-0 whitespace-nowrap items-center justify-center rounded border border-primary/30 bg-primary/10 px-2.5 font-sans text-xs sm:text-sm font-semibold tracking-wider text-primary uppercase'>
                      {granularityLevelLabel}
                    </span>

                    <div className='flex items-center gap-1.5'>
                      <button
                        type='button'
                        onClick={() => {
                          setExplorerWelcomeModalDismissed(globalThis.localStorage, false);
                          setIsWelcomeOpen(true);
                        }}
                        className='inline-flex h-7 cursor-pointer items-center justify-center gap-1 rounded border border-border bg-muted/60 px-2 font-sans text-xs sm:text-sm font-semibold text-foreground transition-colors hover:bg-muted hover:text-primary'
                        title='About Atlas & Guide'
                        aria-label='About Atlas & Guide'>
                        <Info className='size-3.5 shrink-0' />
                        <span className='hidden xl:inline'>About</span>
                      </button>

                      <button
                        type='button'
                        onClick={() => {
                          void handleRefreshAndInvalidate();
                        }}
                        className='inline-flex h-7 cursor-pointer items-center justify-center gap-1 rounded border border-border bg-muted/60 px-2 font-sans text-xs sm:text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-primary'
                        title='Refresh map data'
                        aria-label='Refresh map data'>
                        <RefreshCw className={`size-3.5 shrink-0 ${isFetching ? "animate-spin text-primary" : ""}`} />
                        <span className='hidden xl:inline'>Refresh</span>
                      </button>

                      {isLg && (
                        <button
                          type='button'
                          onClick={() => {
                            setIsOverviewExpanded(false);
                          }}
                          className='inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded border border-border bg-muted/60 p-0 text-muted-foreground transition-colors hover:bg-muted hover:text-card-foreground'
                          title='Collapse overview'
                          aria-label='Collapse overview'>
                          <ChevronUp className='size-4' />
                        </button>
                      )}
                    </div>
                  </div>

                  <h1 className='font-sans text-xl font-bold tracking-tight text-card-foreground leading-tight sm:text-2xl'>
                    {activeMetricLabel}
                  </h1>
                  <p className='mt-1.5 font-sans text-base leading-normal text-muted-foreground'>
                    {activeMetricDescription}
                  </p>

                  {showChangeToggle && (
                    <ExplorerChangeToggle
                      changeMode={isChangeView ? "change" : "absolute"}
                      baseline={baseline}
                      onModeChange={setChangeMode}
                      onBaselineChange={setBaseline}
                      className='mt-3'
                    />
                  )}

                  {isChangeView && mapChange && mapChange.baselinePeriod && (
                    <p className='mt-2 flex items-center gap-1.5 font-sans text-sm text-muted-foreground'>
                      <TrendingUp className='size-3.5 shrink-0 text-primary' />
                      Change from {formatPeriodLabel(mapChange.baselinePeriod)} → {formatPeriodLabel(mapChange.period)}
                    </p>
                  )}

                  {/* Embedded Color Scale Legend */}
                  <div className='mt-4 space-y-2.5 border-t border-border/80 pt-3.5'>
                    {bins.length > 0 && (
                      <div className='space-y-1.5'>
                        <div className='flex h-3.5 w-full overflow-hidden rounded-md border border-border shadow-inner'>
                          {bins.map((bin) => (
                            <div
                              key={bin.color}
                              className='h-full flex-1 transition-opacity hover:opacity-90'
                              style={{ backgroundColor: bin.color }}
                              title={bin.label}
                            />
                          ))}
                        </div>
                        <div className='flex justify-between font-mono text-sm font-bold text-muted-foreground'>
                          <span>{formatViewValue(bins[0]?.min ?? 0)}</span>
                          <span>{formatViewValue(bins.at(-1)?.max ?? 0)}</span>
                        </div>
                      </div>
                    )}

                    {/* Statistical Summary Grid */}
                    {stats && (
                      <div className='grid grid-cols-3 gap-2 font-sans text-base pt-1'>
                        <div className='rounded-md border border-border bg-muted/40 p-2 text-center'>
                          <span className='block text-sm font-medium text-muted-foreground'>
                            Median{isChangeView ? " Δ" : ""}
                          </span>
                          <span className='mt-0.5 block font-mono text-base font-bold text-card-foreground'>
                            {formatViewValue(stats.p50)}
                          </span>
                        </div>
                        <div className='rounded-md border border-border bg-muted/40 p-2 text-center'>
                          <span className='block text-sm font-medium text-muted-foreground'>
                            Mean{isChangeView ? " Δ" : ""}
                          </span>
                          <span className='mt-0.5 block font-mono text-base font-bold text-card-foreground'>
                            {formatViewValue(stats.mean)}
                          </span>
                        </div>
                        <div className='rounded-md border border-border bg-muted/40 p-2 text-center'>
                          <span className='block text-sm font-medium text-muted-foreground'>Std Dev (σ)</span>
                          <span className='mt-0.5 block font-mono text-base font-bold text-card-foreground'>
                            {formatViewValue(stats.stdDev)}
                          </span>
                        </div>
                      </div>
                    )}

                    {!isCompositeActive && activeCoverage && (
                      <CoverageBadge coverage={activeCoverage} suppressedCount={suppressedCount} />
                    )}

                    {!isCompositeActive && (
                      <PeriodTimeline selection={selection} onSelectionChange={onSelectionChange} />
                    )}
                  </div>
                </>
              ) : (
                <div className='flex items-center justify-between gap-2'>
                  <div className='flex items-center gap-2.5 min-w-0 flex-1'>
                    <span className='inline-flex h-7 shrink-0 whitespace-nowrap items-center justify-center rounded border border-primary/30 bg-primary/10 px-2.5 font-sans text-xs font-semibold tracking-wider text-primary uppercase'>
                      {granularityLevelLabel}
                    </span>
                    <h1 className='truncate font-sans text-sm font-bold tracking-tight text-card-foreground'>
                      {activeMetricLabel}
                    </h1>
                  </div>

                  {isLg && (
                    <button
                      type='button'
                      onClick={() => {
                        setIsOverviewExpanded(true);
                      }}
                      className='inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded border border-border bg-muted/60 p-0 text-muted-foreground transition-colors hover:bg-muted hover:text-card-foreground'
                      title='Expand overview'
                      aria-label='Expand overview'>
                      <ChevronDown className='size-4' />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Desktop Hover Tooltip Card */}
            {hoveredInfo && (
              <div className='explorer-surface w-full rounded-lg border border-border p-4 font-sans text-card-foreground shadow-2xl backdrop-blur-2xl transition duration-150 animate-in fade-in-0 zoom-in-95'>
                <div className='border-b border-border pb-2.5'>
                  <p className='text-sm font-semibold tracking-wider text-muted-foreground uppercase'>
                    Hovered {rawGranularityName}
                  </p>
                  <h3 className='mt-0.5 font-sans text-base font-bold text-card-foreground leading-tight'>
                    {hoveredInfo.name}
                  </h3>
                </div>

                <div className='mt-3 space-y-2.5'>
                  <div>
                    <p className='text-sm font-medium text-muted-foreground'>Metric Value</p>
                    <p className='mt-0.5 font-sans text-2xl font-extrabold tracking-tight text-primary'>
                      {formatViewValue(hoveredInfo.value)}
                    </p>
                  </div>

                  {stats && hoveredInfo.value !== null && (
                    <div className='space-y-2 border-t border-border/80 pt-2.5 font-sans text-sm'>
                      <div className='flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1'>
                        <span className='font-medium text-muted-foreground'>Z-Score (Std Dev):</span>
                        <span
                          className={cn(
                            "rounded-xs border px-2 py-0.5 font-mono text-sm font-semibold text-right max-w-full",
                            hoveredInfo.value - stats.mean >= 0
                              ? "border-success/40 bg-success/10 text-success"
                              : "border-destructive/40 bg-destructive/10 text-destructive",
                          )}>
                          {formatZScore(hoveredInfo.value, stats.mean, stats.stdDev)} (
                          {hoveredInfo.value - stats.mean >= 0 ? "+" : "-"}
                          {formatMetricValue(Math.abs(hoveredInfo.value - stats.mean), activeUnit)})
                        </span>
                      </div>

                      <div className='flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 border-t border-border/40 pt-1.5'>
                        <span className='font-medium text-muted-foreground'>vs UK Mean:</span>
                        <span
                          className={cn(
                            "font-mono text-sm font-bold text-right max-w-full",
                            hoveredInfo.value - stats.mean >= 0 ? "text-success" : "text-destructive",
                          )}>
                          {formatZScoreInterpretation(hoveredInfo.value, stats.mean, stats.stdDev, activeUnit)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Desktop Top Right Floating Column: Filters + Regional Comparison Panel (+ Ranking Strip on LG) */}
          <div
            id='tour-map-controls'
            className='absolute top-4 right-4 z-30 flex w-84 max-w-[calc(100vw-2rem)] flex-col items-end gap-2.5 font-sans sm:right-6 sm:w-88'>
            <ExplorerControls
              selection={selection}
              onSelectionChange={onSelectionChange}
              isExpanded={activePanel === "controls"}
              onToggleExpand={handleToggleControls}
              activeTab={controlsTab}
              onActiveTabChange={setControlsTab}
              disableAutoMinimize={joyride.isRunning}
              compositeWeights={compositeWeights}
              onCompositeWeightsChange={onCompositeWeightsChange}
              onResetComposite={handleResetComposite}
            />

            <div id='tour-comparison-flyout' className='w-full'>
              <ExplorerComparisonFlyout
                isOpen={isFlyoutOpen}
                isExpanded={activePanel === "comparison"}
                onToggleExpand={handleToggleComparison}
                onClose={handleCloseComparison}
                onSwapRegions={handleSwapRegionsStable}
                primaryRegion={primaryRegion}
                secondaryRegion={secondaryRegion}
                allValues={flyoutValues}
                metric={metric}
              />
            </div>

            {/* On LG breakpoint (laptops), Ranking Strip renders right under Map Controls column */}
            {isLg && rankingVisible && (
              <div id='tour-ranking-strip' className='w-full'>
                <RankingStrip
                  selection={selection}
                  values={rankingValues}
                  nameById={nameById}
                  formatValue={formatViewValue}
                  pinnedCodes={pinnedCodes}
                  onTogglePin={handleTogglePin}
                  onFocusRegion={handleFocusRegion}
                  collapsed={isRankingCollapsed}
                  onToggleCollapse={() => {
                    setIsRankingCollapsed((prev) => !prev);
                  }}
                  disableAutoMinimize={joyride.isRunning}
                  className='w-full'
                />
              </div>
            )}
          </div>

          {/* Desktop Bottom Center Ranking Strip (Only on XL and 2XL devices) */}
          {!isLg && rankingVisible && (
            <div
              id='tour-ranking-strip'
              className='absolute bottom-4 left-1/2 z-20 w-[min(52rem,calc(100vw-2rem))] -translate-x-1/2'>
              <RankingStrip
                selection={selection}
                values={rankingValues}
                nameById={nameById}
                formatValue={formatViewValue}
                pinnedCodes={pinnedCodes}
                onTogglePin={handleTogglePin}
                onFocusRegion={handleFocusRegion}
                collapsed={isRankingCollapsed}
                onToggleCollapse={() => {
                  setIsRankingCollapsed((prev) => !prev);
                }}
                disableAutoMinimize={joyride.isRunning}
                className='w-full'
              />
            </div>
          )}
        </>
      )}

      {/* Mobile Native Experience (< 768px): Floating Edge Tab Bar & Slide-Up Sheet */}
      {isMobile && (
        <>
          {/* Mobile Bottom Floating Tab Bar */}
          <div className='pointer-events-auto fixed inset-x-3 bottom-5 z-40 flex items-center justify-around rounded-2xl border border-border bg-card/95 p-2 shadow-2xl backdrop-blur-2xl'>
            <button
              type='button'
              id='tour-map-controls'
              onClick={() => {
                setMobileSheetTab(mobileSheetTab === "controls" ? "none" : "controls");
              }}
              className={cn(
                "flex flex-1 flex-col items-center gap-1 rounded-xl py-2 text-sm font-semibold transition cursor-pointer",
                mobileSheetTab === "controls"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}>
              <SlidersHorizontal className='size-4' />
              <span>Controls</span>
            </button>

            <button
              type='button'
              id='tour-overview-card'
              onClick={() => {
                setMobileSheetTab(mobileSheetTab === "overview" ? "none" : "overview");
              }}
              className={cn(
                "flex flex-1 flex-col items-center gap-1 rounded-xl py-2 text-sm font-semibold transition cursor-pointer",
                mobileSheetTab === "overview"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}>
              <Layers className='size-4' />
              <span>Overview</span>
            </button>

            <button
              type='button'
              id='tour-comparison-flyout'
              onClick={() => {
                setMobileSheetTab(mobileSheetTab === "comparison" ? "none" : "comparison");
              }}
              className={cn(
                "relative flex flex-1 flex-col items-center gap-1 rounded-xl py-2 text-sm font-semibold transition cursor-pointer",
                mobileSheetTab === "comparison"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}>
              <ArrowRightLeft className='size-4' />
              <span>Compare</span>
              {primaryRegion && (
                <span className='absolute top-1 right-3 flex size-2.5 rounded-full bg-primary ring-2 ring-background' />
              )}
            </button>
          </div>

          {/* Mobile Slide-Up Drawer / Sheet Overlay */}
          {mobileSheetTab !== "none" && (
            <div
              className='fixed inset-0 isolate z-50 flex flex-col justify-end bg-black/50 animate-in fade-in-0'
              role='presentation'
              onClick={(e) => {
                if (e.target === e.currentTarget) setMobileSheetTab("none");
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") setMobileSheetTab("none");
              }}>
              <div className='max-h-[80vh] w-full overflow-y-auto overscroll-contain rounded-t-3xl border-t border-border bg-card p-5 text-card-foreground shadow-2xl animate-in slide-in-from-bottom-full'>
                {/* Drag Indicator & Header Bar */}
                <div className='mb-4 flex items-center justify-between border-b border-border pb-3'>
                  <div className='flex items-center gap-2'>
                    <div className='h-1.5 w-10 rounded-full bg-muted' />
                    <span className='text-sm font-bold tracking-wider text-muted-foreground uppercase'>
                      {mobileSheetTab === "controls"
                        ? "Map Controls & Metrics"
                        : mobileSheetTab === "overview"
                          ? "Dataset Overview"
                          : "Regional Comparison"}
                    </span>
                  </div>

                  <button
                    type='button'
                    onClick={() => {
                      setMobileSheetTab("none");
                    }}
                    className='cursor-pointer rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
                    aria-label='Close drawer'>
                    <X className='size-5' />
                  </button>
                </div>

                {/* Mobile Tab 1: Controls */}
                {mobileSheetTab === "controls" && (
                  <ExplorerControls
                    selection={selection}
                    onSelectionChange={(sel) => {
                      onSelectionChange(sel);
                      setMobileSheetTab("none");
                    }}
                    isExpanded
                    variant='sheet'
                    activeTab={controlsTab}
                    onActiveTabChange={setControlsTab}
                    disableAutoMinimize={joyride.isRunning}
                    compositeWeights={compositeWeights}
                    onCompositeWeightsChange={onCompositeWeightsChange}
                    onResetComposite={() => {
                      onCompositeWeightsChange({});
                    }}
                  />
                )}

                {/* Mobile Tab 2: Overview & Legend */}
                {mobileSheetTab === "overview" && (
                  <div className='space-y-4 text-card-foreground'>
                    <div className='flex items-center justify-between gap-2'>
                      <span className='inline-flex items-center rounded border border-primary/30 bg-primary/10 px-2 py-1 text-sm font-semibold tracking-wider text-primary uppercase'>
                        {granularityLevelLabel}
                      </span>
                      <button
                        type='button'
                        onClick={() => {
                          setMobileSheetTab("none");
                          setExplorerWelcomeModalDismissed(globalThis.localStorage, false);
                          setIsWelcomeOpen(true);
                        }}
                        className='flex cursor-pointer items-center gap-1 rounded border border-border bg-muted/60 px-2.5 py-1 text-sm font-semibold text-foreground transition-colors hover:bg-muted'>
                        <Info className='size-3.5' /> About Atlas
                      </button>
                    </div>

                    <h2 className='text-xl font-bold tracking-tight text-card-foreground'>{activeMetricLabel}</h2>
                    <p className='text-sm text-muted-foreground leading-relaxed'>{activeMetricDescription}</p>

                    {showChangeToggle && (
                      <ExplorerChangeToggle
                        changeMode={isChangeView ? "change" : "absolute"}
                        baseline={baseline}
                        onModeChange={setChangeMode}
                        onBaselineChange={setBaseline}
                      />
                    )}

                    {isChangeView && mapChange && mapChange.baselinePeriod && (
                      <p className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                        <TrendingUp className='size-3.5 shrink-0 text-primary' />
                        Change from {formatPeriodLabel(mapChange.baselinePeriod)} →{" "}
                        {formatPeriodLabel(mapChange.period)}
                      </p>
                    )}

                    {/* Color Bins Legend */}
                    {bins.length > 0 && (
                      <div className='space-y-1.5 border-t border-border pt-3'>
                        <div className='flex h-4 w-full overflow-hidden rounded-md border border-border'>
                          {bins.map((bin) => (
                            <div key={bin.color} className='h-full flex-1' style={{ backgroundColor: bin.color }} />
                          ))}
                        </div>
                        <div className='flex justify-between font-mono text-sm font-bold text-muted-foreground'>
                          <span>{formatViewValue(bins[0]?.min ?? 0)}</span>
                          <span>{formatViewValue(bins.at(-1)?.max ?? 0)}</span>
                        </div>
                      </div>
                    )}

                    {/* Stats Grid */}
                    {stats && (
                      <div className='grid grid-cols-3 gap-2 text-center pt-2'>
                        <div className='rounded-lg border border-border bg-muted/40 p-2.5'>
                          <span className='block text-sm text-muted-foreground'>Median{isChangeView ? " Δ" : ""}</span>
                          <span className='block font-mono text-sm font-bold text-card-foreground mt-0.5'>
                            {formatViewValue(stats.p50)}
                          </span>
                        </div>
                        <div className='rounded-lg border border-border bg-muted/40 p-2.5'>
                          <span className='block text-sm text-muted-foreground'>Mean{isChangeView ? " Δ" : ""}</span>
                          <span className='block font-mono text-sm font-bold text-card-foreground mt-0.5'>
                            {formatViewValue(stats.mean)}
                          </span>
                        </div>
                        <div className='rounded-lg border border-border bg-muted/40 p-2.5'>
                          <span className='block text-sm text-muted-foreground'>Std Dev</span>
                          <span className='block font-mono text-sm font-bold text-card-foreground mt-0.5'>
                            {formatViewValue(stats.stdDev)}
                          </span>
                        </div>
                      </div>
                    )}

                    {!isCompositeActive && activeCoverage && (
                      <CoverageBadge
                        coverage={activeCoverage}
                        suppressedCount={suppressedCount}
                        className='border-t border-border pt-3'
                      />
                    )}

                    {!isCompositeActive && (
                      <PeriodTimeline selection={selection} onSelectionChange={onSelectionChange} />
                    )}

                    {rankingVisible && (
                      <div id='tour-ranking-strip'>
                        <RankingStrip
                          selection={selection}
                          values={rankingValues}
                          nameById={nameById}
                          formatValue={formatViewValue}
                          pinnedCodes={pinnedCodes}
                          onTogglePin={handleTogglePin}
                          onFocusRegion={handleFocusRegion}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Mobile Tab 3: Comparison */}
                {mobileSheetTab === "comparison" && (
                  <ExplorerComparisonFlyout
                    isOpen
                    isExpanded
                    variant='sheet'
                    onToggleExpand={() => {}}
                    onClose={() => {
                      setMobileSheetTab("none");
                    }}
                    onSwapRegions={handleSwapRegionsStable}
                    primaryRegion={primaryRegion}
                    secondaryRegion={secondaryRegion}
                    allValues={flyoutValues}
                    metric={metric}
                  />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
